/**
 * Where to send someone after they sign in.
 *
 * `/login?next=/cbt/lobby/<id>` lets a guest who was stopped at a sign-in gate
 * land back on the page they were trying to reach. Only same-site absolute
 * paths are honoured ("/foo"), never "//evil.com" or "/\evil.com", which
 * browsers treat as protocol-relative URLs — anything else falls back to the
 * dashboard so the param can't be used as an open redirect.
 */
// eslint-disable-next-line no-control-regex
const SAFE_PATH = /^\/(?![/\\])[^\s\x00-\x1f\x7f\\]*$/;

export function getSafeRedirect(next: unknown, fallback = "/dashboard"): string {
  const value = Array.isArray(next) ? next[0] : next;
  if (typeof value !== "string") return fallback;
  // One leading "/", then no "/" or "\" straight after it, and no whitespace,
  // control characters or backslashes anywhere: browsers strip tabs/newlines
  // from URLs, so "/\t/evil.com" would otherwise collapse to "//evil.com".
  return SAFE_PATH.test(value) ? value : fallback;
}

/**
 * Carry a (validated) `next` onto the following step of a multi-page flow,
 * e.g. signup → OTP → onboarding, so it survives to the final redirect.
 * Drops it silently if it isn't a safe same-site path.
 */
export function withNext(path: string, next: unknown): string {
  const safe = getSafeRedirect(next, "");
  if (!safe) return path;
  return `${path}${path.includes("?") ? "&" : "?"}next=${encodeURIComponent(safe)}`;
}

/** `/login?next=<path>` for a page that needs a signed-in user. */
export const loginHref = (next: string) => withNext("/login", next);

/** `/signup?next=<path>`: new students land back on `next` once onboarded. */
export const signupHref = (next: string) => withNext("/signup", next);
